// Informes por puesto (lote D, M49): tipos, etiquetas y constantes.
// Ids internos del Checklist: GT 5 = GT-05 (más GT-15, GT-16 y GT-17 por agente), GT 6 = GT-06, GT 7 = GT-07,
// GT 37 = GT-09, TH 1 = TH-01. Decidido por Claude; lo revisa Kevin al validar.

export type TipoInforme = 'agentes' | 'riesgos' | 'medidas';
export const TIPOS_INFORME: TipoInforme[] = ['agentes', 'riesgos', 'medidas'];

/** Número legal del Anexo 1 (lo que ve el cliente). */
export const LEGAL_INFORMES: Record<string, string> = {
  'GT-05': 'GT 5', 'GT-06': 'GT 6', 'GT-07': 'GT 7', 'GT-09': 'GT 37', 'GT-14': 'GT 9', 'TH-01': 'TH 1',
  'GT-15': 'GT 5 (agentes físicos)', 'GT-16': 'GT 5 (agentes químicos)', 'GT-17': 'GT 5 (agentes biológicos)',
};

export const ITEM_DE_TIPO: Record<TipoInforme, string> = { agentes: 'GT-05', riesgos: 'GT-06', medidas: 'GT-07' };

export const ETIQUETA_TIPO_INFORME: Record<TipoInforme, string> = {
  agentes: 'Informe de medición de agentes físicos, químicos y biológicos',
  riesgos: 'Informe de evaluación de riesgos de seguridad, ergonómicos y psicosociales',
  medidas: 'Informe de medidas de prevención implementadas',
};
export const CORTO_TIPO_INFORME: Record<TipoInforme, string> = {
  agentes: 'Agentes (GT 5)', riesgos: 'Riesgos (GT 6)', medidas: 'Medidas (GT 7)',
};

/** Vigencia sugerida por StorSafe (no normativa). */
export const MESES_VIGENCIA = 12;
/** Días de un informe vencido que todavía cuentan como "En proceso" (sugeridos por StorSafe, no normativos). */
export const DIAS_TOLERANCIA = 30;
export const NOTA_PLAZOS = `Plazos sugeridos por StorSafe, no normativos: vigencia de ${MESES_VIGENCIA} meses y ${DIAS_TOLERANCIA} días de tolerancia.`;

export type Modulo = 'iper' | 'reba' | 'rula' | 'rosa' | 'niosh' | 'snook' | 'ruido' | 'iluminacion' | 'estres_termico'
  | 'quimico_tarea' | 'biogaval' | 'psicosocial';
export type Agente = 'fisico' | 'quimico' | 'biologico' | 'ergonomico' | 'psicosocial' | 'seguridad';

export const ETIQUETA_AGENTE: Record<Agente, string> = {
  fisico: 'Agentes físicos', quimico: 'Agentes químicos', biologico: 'Agentes biológicos', ergonomico: 'Riesgos ergonómicos',
  psicosocial: 'Riesgos psicosociales', seguridad: 'Riesgos de seguridad',
};
export const ETIQUETA_MODULO: Record<Modulo, string> = {
  iper: 'Matriz de Riesgos', reba: 'REBA', rula: 'RULA', rosa: 'ROSA', niosh: 'Ecuación de NIOSH', snook: 'Snook y Ciriello',
  ruido: 'Ruido', iluminacion: 'Iluminación', estres_termico: 'Estrés térmico', quimico_tarea: 'Riesgo químico por tarea',
  biogaval: 'BIOGAVAL', psicosocial: 'Riesgo psicosocial',
};

/** Qué evaluaciones entran en cada informe. GT 5: mediciones de agentes; GT 6: evaluaciones de riesgo. */
export const MODULOS_DE_TIPO: Record<'agentes' | 'riesgos', Modulo[]> = {
  agentes: ['ruido', 'iluminacion', 'estres_termico', 'quimico_tarea', 'biogaval'],
  riesgos: ['iper', 'reba', 'rula', 'rosa', 'niosh', 'snook', 'psicosocial', 'quimico_tarea', 'biogaval'],
};
export const AGENTE_DE_ITEM: Record<string, Agente> = { 'GT-15': 'fisico', 'GT-16': 'quimico', 'GT-17': 'biologico' };

/** Una evaluación o medición existente del puesto, con lo que el informe necesita. */
export interface Evaluacion {
  id: string;
  modulo: Modulo;
  agente: Agente;
  fecha: string;             // YYYY-MM-DD (fecha de la evaluación o de registro)
  cambio: string;            // fecha y hora del último cambio conocido (ISO)
  titulo: string;            // descripción o local
  metodologia: string;
  resultado: string;         // resultado en palabras
  referencia: string;        // valor de referencia que usa el módulo
  cumple: boolean | null;    // frente a la referencia (null = sin criterio)
}

export type NivelControl = 'eliminacion' | 'sustitucion' | 'ingenieria' | 'administrativo' | 'epp';
export const ORDEN_JERARQUIA: NivelControl[] = ['eliminacion', 'sustitucion', 'ingenieria', 'administrativo', 'epp'];
export const ETIQUETA_NIVEL: Record<NivelControl, string> = {
  eliminacion: 'Eliminación', sustitucion: 'Sustitución', ingenieria: 'Controles de ingeniería',
  administrativo: 'Controles administrativos', epp: 'Equipos de protección personal',
};
/** Columna de la Matriz de Riesgos que guarda la medida de cada nivel. */
export const COLUMNA_IPER: Record<NivelControl, string> = {
  eliminacion: 'medida_eliminacion', sustitucion: 'medida_sustitucion', ingenieria: 'medida_control_ingenieria',
  administrativo: 'medida_control_administrativo', epp: 'medida_epp',
};

export type OrigenMedida = 'iper' | 'incidente' | 'puesto';
export type EstadoMedida = 'pendiente' | 'en_curso' | 'cerrada';
export const ETIQUETA_ORIGEN_MEDIDA: Record<OrigenMedida, string> = {
  iper: 'Matriz de Riesgos', incidente: 'Incidentes', puesto: 'Propia del puesto',
};
export const ETIQUETA_ESTADO_MEDIDA: Record<EstadoMedida, string> = { pendiente: 'Pendiente', en_curso: 'En curso', cerrada: 'Cerrada' };

/** Medida del puesto lista para mostrar (origen ya resuelto, sin duplicar datos). */
export interface MedidaVista {
  id: string;                 // id de position_measures
  origen: OrigenMedida;
  nivel: NivelControl | null;
  descripcion: string;
  responsable: string;
  compromiso: string | null;  // fecha compromiso
  seguimiento: string;
  resultado: string;
  estado: EstadoMedida;
  cerrada_el: string | null;
  evidencia: string | null;   // nombre del archivo
  prioritaria: boolean;       // medida para el grupo de atención prioritaria
}

// ── Filas de la base (M49) ──
export interface FilaInforme {
  id: string; company_id: string; job_position_id: string | null; position_name: string; kind: TipoInforme;
  conclusions: string | null; recommendations: string | null; created_at: string; updated_at: string;
}
export type MetodoAprobacion = 'pantalla' | 'papel';
export interface FilaVersion {
  // procedimiento_epp (M51, PO 21): el procedimiento de adquisición de EPP, con epp_procedure_id.
  id: string; company_id: string; doc_type: 'informe_puesto' | 'procedimiento' | 'procedimiento_epp'; report_id: string | null; procedure_id: string | null;
  epp_procedure_id?: string | null;
  job_position_id: string | null; report_kind: TipoInforme | null; permit_type: string | null; version: number;
  snapshot: Record<string, unknown>; content_hash: string; eval_fingerprint: string | null; sign_method: MetodoAprobacion;
  signer_name: string | null; signer_cedula: string | null; signature_path: string | null; scan_path: string | null;
  approved_at: string; approved_by: string | null;
}
export type TipoAdjunto = 'calibracion' | 'laboratorio' | 'foto';
export const ETIQUETA_ADJUNTO: Record<TipoAdjunto, string> = {
  calibracion: 'Certificado de calibración', laboratorio: 'Informe externo de laboratorio', foto: 'Foto',
};
export interface FilaAdjunto {
  id: string; company_id: string; report_id: string; kind: TipoAdjunto; title: string; file_path: string; file_name: string;
  file_mime: string; file_size: number; agent_type: 'quimico' | 'biologico' | null; agent_name: string | null;
  result_value: number | null; limit_value: number | null; unit: string | null; limit_source: string | null; measured_at: string | null;
  provider: string | null; equipment: string | null; serial_number: string | null; valid_until: string | null; created_at: string;
}
export interface FilaMedida {
  id: string; company_id: string; job_position_id: string | null; source: OrigenMedida; iper_assessment_id: string | null;
  incident_action_id: string | null; control_level: NivelControl | null; description: string | null; for_priority_group: boolean;
  responsible_name: string | null; due_date: string | null; follow_up: string | null; result: string | null; status: EstadoMedida;
  closed_at: string | null; evidence_path: string | null; evidence_name: string | null; created_at: string;
}

// ── Verificación in situ de las medidas implementadas (GT 9 = GT-14) ──
export type ResultadoVerificacion = 'eficaz' | 'no_eficaz';
export const ETIQUETA_RESULTADO_VERIFICACION: Record<ResultadoVerificacion, string> = { eficaz: 'Eficaz', no_eficaz: 'No eficaz' };
export interface FilaVerificacion {
  id: string; company_id: string; measure_id: string; verified_on: string; result: ResultadoVerificacion; evidence_path: string;
  evidence_name: string; inspection_id: string | null; notes: string | null; created_by: string | null; created_at: string;
}
export const NOTA_VERIFICACION = `Plazos sugeridos por StorSafe, no normativos: una verificación eficaz en los últimos ${MESES_VIGENCIA} meses por medida y ${DIAS_TOLERANCIA} días desde la implementación para registrarla.`;

/** Adjunto como queda en el contenido congelado. */
export interface AdjuntoContenido {
  id: string; tipo: TipoAdjunto; titulo: string; archivo: string;
  agente?: string | null; tipo_agente?: 'quimico' | 'biologico' | null; resultado?: number | null; limite?: number | null;
  unidad?: string | null; fuente_limite?: string | null; fecha?: string | null; cumple?: boolean | null;
  proveedor?: string | null; equipo?: string | null; serie?: string | null; vigente_hasta?: string | null;
}

/** Contenido congelado de una versión aprobada del informe de un puesto (lo que se firma). */
export interface ContenidoInforme {
  informe_id: string;
  tipo: TipoInforme;
  empresa: string;
  puesto_id: string;
  puesto: string;
  fecha_corte: string;
  expuestos: number;
  evaluaciones: Omit<Evaluacion, 'cambio'>[];
  adjuntos: AdjuntoContenido[];
  medidas: MedidaVista[];
  conclusiones: string;
  recomendaciones: string;
  /** Solo un número y las medidas del puesto; nunca nombres ni tipo de condición. */
  atencion_prioritaria: { trabajadores: number; medidas: number } | null;
}

export const TEXTO_FIRMA_APROBACION =
  'Firma electrónica simple del responsable de SSO (Ley de Comercio Electrónico, Firmas Electrónicas y Mensajes de Datos, Art. 14): '
  + 'trazo, nombre, cédula, fecha y hora del servidor y huella SHA-256 del contenido aprobado.';

export const BUCKET_INFORMES = 'sst-informes';
